import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { COOKIE_TEXT } from "../i18n/legalContent";
import { MYSYMP_SOCIALS, WINE_SOCIALS } from "../lib/socials";

const SOCIAL_LABEL = { de: "Folgen", en: "Follow", fr: "Suivre" };

const SocialIcons = ({ items, group }) => (
  <div className="flex flex-wrap gap-2.5" data-testid={`social-${group}`}>
    {items.map(({ name, url, Icon }) => (
      <a
        key={`${group}-${name}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${group === "wine" ? "Wine Pairing" : "MYSYMP"} ${name}`}
        data-testid={`social-${group}-${name.toLowerCase()}`}
        className="flex h-10 w-10 items-center justify-center border border-white/15 text-white/55 transition-colors hover:border-champagne hover:text-champagne"
      >
        <Icon size={17} strokeWidth={1.6} />
      </a>
    ))}
  </div>
);

const Footer = () => {
  const { t, lang } = useLang();
  const year = new Date().getFullYear();
  const cookie = COOKIE_TEXT[lang] || COOKIE_TEXT.de;
  const openCookieSettings = () => window.dispatchEvent(new Event("mysymp:open-cookie-settings"));

  const explore = [
    { to: "/interior", label: t.nav.interior },
    { to: "/fashion", label: t.nav.fashion },
    { to: "/wine", label: t.nav.wine },
    { to: "/real-estate", label: t.nav.realEstate },
    { to: "/consulting", label: t.nav.consulting },
    { to: "/library", label: t.nav.library },
    { to: "/live-products", label: t.nav.products },
    { to: "/shop", label: t.nav.shop },
  ];
  const legal = [
    { to: "/impressum", label: t.footer.impressum },
    { to: "/datenschutz", label: t.footer.privacy },
    { to: "/agb", label: t.footer.terms },
  ];

  return (
    <footer className="border-t border-white/10 bg-obsidian pt-24 md:pt-32" data-testid="footer">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-2">
              <img src="/mysymp-logo-gold.png" alt="MYSYMP AG" className="h-9 w-auto" />
              <span className="text-xs uppercase tracking-[0.3em] text-champagne">AG</span>
            </div>
            <p className="mt-6 max-w-sm text-base font-light leading-relaxed text-white/50">{t.footer.tagline}</p>

            <div className="mt-10 space-y-5">
              <div>
                <span className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-champagne">{SOCIAL_LABEL[lang]} · Isidoro Celentano</span>
                <SocialIcons items={MYSYMP_SOCIALS} group="mysymp" />
              </div>
              <div>
                <span className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-champagne">{SOCIAL_LABEL[lang]} · Wine Pairing</span>
                <SocialIcons items={WINE_SOCIALS} group="wine" />
              </div>
            </div>

            <p className="mt-8 text-xs uppercase tracking-[0.25em] text-white/40">{t.footer.built}</p>
          </div>

          <div className="lg:col-span-3">
            <h4 className="mb-6 text-xs uppercase tracking-[0.25em] text-champagne">{t.footer.explore}</h4>
            <ul className="space-y-3">
              {explore.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="link-sweep text-sm text-white/55 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="mb-6 text-xs uppercase tracking-[0.25em] text-champagne">{t.footer.company}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" data-testid="footer-link-about" className="link-sweep text-sm text-white/55 hover:text-white transition-colors">
                  {t.nav.about}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="link-sweep text-sm text-white/55 hover:text-white transition-colors">
                  {t.nav.contact}
                </Link>
              </li>
              {legal.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.to.replace("/", "")}`} className="link-sweep text-sm text-white/55 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-20 overflow-hidden border-t border-white/5 py-10">
          <div className="font-serif text-[16vw] font-light leading-none tracking-tighter text-white/[0.04] md:text-[13vw]">
            MYSYMP AG
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-5 border-t border-white/5 py-8 text-xs text-white/35 md:flex-row">
          <span>© {year} MYSYMP AG. {t.footer.rights}</span>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/impressum" data-testid="footer-bottom-impressum" className="link-sweep uppercase tracking-[0.2em] text-white/50 hover:text-champagne transition-colors">
              {t.footer.impressum}
            </Link>
            <Link to="/datenschutz" data-testid="footer-bottom-datenschutz" className="link-sweep uppercase tracking-[0.2em] text-white/50 hover:text-champagne transition-colors">
              {t.footer.privacy}
            </Link>
            <Link to="/agb" data-testid="footer-bottom-agb" className="link-sweep uppercase tracking-[0.2em] text-white/50 hover:text-champagne transition-colors">
              {t.footer.terms}
            </Link>
            <button type="button" onClick={openCookieSettings} data-testid="footer-cookie-settings" className="link-sweep uppercase tracking-[0.2em] text-white/50 hover:text-champagne transition-colors">
              {cookie.settings}
            </button>
            <span className="flex items-center gap-2 uppercase tracking-[0.2em] text-champagne/80">
              <ShieldCheck size={13} /> {t.footer.compliant}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
